import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  ExternalLink,
  Layers3,
  Lightbulb,
  Palette,
  Ruler,
  Sparkles,
  Shapes,
  X,
} from "lucide-react";
import "./ChannelLetters.css";

const ASSET_BASE = "/images/channel-letters";
const SPEC_BASE = ASSET_BASE;

const categoryLinks = [
  { number: "01", label: "チャンネル文字", count: "6種", target: "channel-letters" },
  { number: "02", label: "LEDチャンネル文字", count: "12種", target: "led-channel-letters" },
  { number: "03", label: "LEDネオン・3D", count: "11種", target: "led-neon-3d" },
];

const filters = [
  "素材・仕上げ",
  "正面発光",
  "背面発光",
  "側面発光",
  "両面発光",
  "LEDネオン",
  "3D表現",
  "屋内・屋外",
];

const heroFeatures = [
  {
    icon: Layers3,
    title: "素材・仕上げを選択",
    text: "金属・樹脂・アクリルなど",
  },
  {
    icon: Lightbulb,
    title: "発光方式を選択",
    text: "正面・背面・側面・両面など",
  },
  {
    icon: Building2,
    title: "屋内・屋外仕様",
    text: "設置環境に応じた製品を選択",
  },
  {
    icon: Ruler,
    title: "受注生産",
    text: "文字・ロゴ・サイズに合わせて製作",
  },
];

const materialFeatures = [
  {
    icon: Layers3,
    title: "多彩な素材",
    text: "ステンレス・真鍮・樹脂・カルプ・アクリルなど",
  },
  {
    icon: Palette,
    title: "表面仕上げ",
    text: "製品により塗装・めっきなどの仕上げに対応",
  },
  {
    icon: Building2,
    title: "設置環境",
    text: "屋内・屋外対応製品から選択",
  },
];

const ledFeatures = [
  {
    icon: Lightbulb,
    title: "選べる発光方式",
    text: "正面・背面・側面・両面など",
  },
  {
    icon: Sparkles,
    title: "多様な文字表現",
    text: "スリム、ネオン、マーキーなど",
  },
  {
    icon: Ruler,
    title: "用途に応じた仕様",
    text: "文字厚・サイズ・設置条件に合わせて選択",
  },
];

const neon3dFeatures = [
  {
    icon: Shapes,
    title: "3D形状",
    text: "垂直側面・テーパー・曲面などの立体表現",
  },
  {
    icon: Palette,
    title: "素材・表面表現",
    text: "カラー樹脂、PC板、シルク調、パターンなど",
  },
  {
    icon: Lightbulb,
    title: "光の演出",
    text: "側面発光、LEDネオン、ミラーによる奥行き表現など",
  },
];

const channelLetters = [
  {
    name: "箱文字",
    englishName: "BOX LETTER",
    image: "box-letter.png",
    detailsImage: "box-letter-spec.png",
    description: "金属板を立体的に加工して製作する文字",
    specification: "文字厚 30〜60／80／100／120mm",
    note: "最小文字高 100mm〜　屋内・屋外用",
    tags: ["素材・仕上げ", "屋内・屋外"],
  },
  {
    name: "真鍮文字",
    englishName: "BRASS LETTER",
    image: "brass-letter.png",
    detailsImage: "brass-letter-spec.png",
    description: "真鍮・銅・赤銅の質感を生かした金属文字",
    specification: "文字厚 3／5／10／20mm",
    note: "最小文字高 20mm〜　屋内用",
    tags: ["素材・仕上げ"],
  },
  {
    name: "ステンレス文字",
    englishName: "STAINLESS STEEL LETTER",
    image: "stainless-letter.png",
    detailsImage: "stainless-letter-spec.png",
    description: "ヘアラインや塗装などの仕上げに対応",
    specification: "文字厚 3／5／8／10mm",
    note: "最小文字高 20mm〜　屋内・屋外用",
    tags: ["素材・仕上げ", "屋内・屋外"],
  },
  {
    name: "樹脂メタリック文字",
    englishName: "METALLIC RESIN LETTER",
    image: "resin-metallic-letter.png",
    detailsImage: "resin-metallic-letter-spec.png",
    description: "樹脂文字にメタリック調の仕上げ",
    specification: "文字厚 5／8／10／15mm",
    note: "最小文字高 150mm〜　屋内・屋外用",
    tags: ["素材・仕上げ", "屋内・屋外"],
  },
  {
    name: "カルプ文字",
    englishName: "CALP LETTER",
    image: "calp-letter.png",
    detailsImage: "calp-letter-spec.png",
    description: "軽量なカルプ材を使用した立体文字",
    specification: "文字厚 10／20／30／50mm",
    note: "最小文字高 30mm〜　屋内・屋外用",
    tags: ["素材・仕上げ", "屋内・屋外"],
  },
  {
    name: "アクリル文字",
    englishName: "ACRYLIC LETTER",
    image: "acrylic-letter.png",
    detailsImage: "acrylic-letter-spec.png",
    description: "アクリル板を文字やロゴの形に加工",
    specification: "文字厚 3／5／8／10／15mm",
    note: "最小文字高 20mm〜　屋内・屋外用",
    tags: ["素材・仕上げ", "屋内・屋外"],
  },
];

const ledChannelLetters = [
  {
    name: "樹脂チャンネル文字",
    englishName: "RESIN CHANNEL LETTER",
    image: "resin-channel.png",
    detailsImage: "resin-channel-spec.png",
    description: "継ぎ目のない発光面",
    specification: "文字厚 30〜60／80／100／120mm",
    tags: ["正面発光", "屋内・屋外"],
  },
  {
    name: "正面発光チャンネル文字",
    englishName: "FRONT-LIT CHANNEL LETTER",
    image: "front-lit-channel.png",
    detailsImage: "front-lit-channel-spec.png",
    description: "文字の正面が均一に発光",
    specification: "文字厚 40〜60／80／100／120mm",
    tags: ["正面発光", "屋内・屋外"],
  },
  {
    name: "バックライトチャンネル文字",
    englishName: "BACK-LIT CHANNEL LETTER",
    image: "backlit-channel.png",
    detailsImage: "backlit-channel-spec.png",
    description: "背面から壁面を照らす発光",
    specification: "文字厚 30／60／80／100／150mm",
    tags: ["背面発光", "屋内・屋外"],
  },
  {
    name: "両面チャンネル文字",
    englishName: "DOUBLE-LIT CHANNEL LETTER",
    image: "double-lit-channel.png",
    detailsImage: "double-lit-channel-spec.png",
    description: "正面と背面の両方が発光",
    specification: "文字厚 40〜60／80／100／120mm",
    tags: ["正面発光", "背面発光", "両面発光", "屋内・屋外"],
  },
  {
    name: "側面発光チャンネル文字",
    englishName: "SIDE-LIT CHANNEL LETTER",
    image: "side-lit-channel.png",
    detailsImage: "side-lit-channel-spec.png",
    description: "文字の側面だけが発光",
    specification: "文字厚 20／30／50mm",
    tags: ["側面発光", "屋内・屋外"],
  },
  {
    name: "縁ありチャンネル文字",
    englishName: "BORDERED CHANNEL LETTER",
    image: "bordered-channel.png",
    detailsImage: "bordered-channel-spec.png",
    description: "大型サイン向けの押縁構造",
    specification: "文字厚 60／80／100／150mm",
    tags: ["正面発光", "屋内・屋外"],
  },
  {
    name: "レター（スリム）",
    englishName: "SLIM LETTER",
    image: "slim-letter.png",
    detailsImage: "slim-letter-spec.png",
    description: "細かな文字表現に対応",
    specification: "文字厚 20〜30mm",
    tags: ["正面発光"],
  },
  {
    name: "レター（ネオン）",
    englishName: "NEON LETTER",
    image: "letter-neon.jpg",
    detailsImage: "letter-neon-spec.png",
    description: "ネオンのような線状発光",
    specification: "文字厚 20〜30mm",
    tags: ["LEDネオン"],
  },
  {
    name: "カラーアンドホワイト",
    englishName: "COLOR & WHITE",
    image: "color-and-white.png",
    detailsImage: "color-and-white-spec.png",
    description: "昼と夜で見え方が変わる仕様",
    specification: "カラーチェンジアクリル",
    tags: ["正面発光"],
  },
  {
    name: "マーキーサイン（電球タイプ）",
    englishName: "MARQUEE SIGN / BULB TYPE",
    image: "marquee-bulb.png",
    detailsImage: "marquee-bulb-spec.png",
    description: "電球の光でレトロな表情を演出",
    specification: "最小文字高 150mm",
    tags: ["正面発光"],
  },
  {
    name: "マーキーサイン（ドットタイプ）",
    englishName: "MARQUEE SIGN / DOT TYPE",
    image: "marquee-dot.png",
    detailsImage: "marquee-dot-spec.png",
    description: "ドット状の光で輪郭を表現",
    specification: "最小文字高 100mm",
    tags: ["正面発光"],
  },
  {
    name: "チューブネオンサイン",
    englishName: "TUBE NEON SIGN",
    image: "led-neon-channel.png",
    detailsImage: "led-neon-channel-spec.jpg",
    description: "PVCチューブによる線状発光",
    specification: "チューブ幅 6／8／10／12mm",
    tags: ["LEDネオン", "屋内・屋外"],
  },
];

const ledNeon3d = [
  {
    name: "側面発光",
    englishName: "SIDE-LIT 3D",
    image: "neon-side.jpg",
    detailsImage: "neon-side-spec.png",
    description: "側面を垂直に見せる3D構造",
    tags: ["側面発光", "3D表現"],
  },
  {
    name: "側面発光（テーパー）",
    englishName: "TAPERED SIDE-LIT 3D",
    image: "neon-taper.jpg",
    detailsImage: "neon-taper-spec.png",
    description: "側面に傾斜をつけた立体表現",
    tags: ["側面発光", "3D表現"],
  },
  {
    name: "3Dインフィニティミラー",
    englishName: "3D INFINITY MIRROR",
    image: "neon-infinity.jpg",
    detailsImage: "neon-infinity-spec.png",
    description: "鏡とLED照明による奥行き表現",
    tags: ["LEDネオン", "3D表現"],
  },
  {
    name: "カラー樹脂3Dチャンネル",
    englishName: "COLORED RESIN 3D CHANNEL",
    image: "neon-color-resin.jpg",
    detailsImage: "neon-color-resin-spec.png",
    description: "カラー樹脂の色と立体感を活用",
    tags: ["3D表現"],
  },
  {
    name: "PC一体型",
    englishName: "PC INTEGRATED 3D",
    image: "neon-pc.jpg",
    detailsImage: "neon-pc-spec.png",
    description: "印刷デザインと3D形状を一体化",
    tags: ["3D表現"],
  },
  {
    name: "3Dモデリング照明",
    englishName: "3D MODELING LIGHT",
    image: "neon-modeling.jpg",
    detailsImage: "neon-modeling-spec.png",
    description: "立体形状を生かした照明製作",
    tags: ["3D表現"],
  },
  {
    name: "3Dガルバフレーム",
    englishName: "3D GALVA FRAME",
    image: "neon-galva.jpg",
    detailsImage: "neon-galva-spec.png",
    description: "3Dフレーム内にLEDネオンを配置",
    tags: ["LEDネオン", "3D表現"],
  },
  {
    name: "表シート貼り",
    englishName: "SURFACE SHEET TYPE",
    image: "neon-sheet.jpg",
    detailsImage: "neon-sheet-spec.png",
    description: "遮光シートで発光面を調整",
    tags: ["LEDネオン", "3D表現"],
  },
  {
    name: "シルクタイプ3D",
    englishName: "SILK TYPE 3D",
    image: "neon-silk.jpg",
    detailsImage: "neon-silk-spec.png",
    description: "フィラメントによる独特の質感",
    tags: ["3D表現"],
  },
  {
    name: "側面3D",
    englishName: "SIDE 3D",
    image: "neon-side3d.jpg",
    detailsImage: "neon-side3d-spec.png",
    description: "側面の凹凸・曲線で立体感を強調",
    tags: ["側面発光", "3D表現"],
  },
  {
    name: "パターン模様3D",
    englishName: "PATTERN 3D",
    image: "neon-pattern.jpg",
    detailsImage: "neon-pattern-spec.png",
    description: "表面パターンを3D質感で再現",
    tags: ["3D表現"],
  },
];

function FeatureRow({ features, compact = false, light = false }) {
  return (
    <div
      className={`channel-feature-row${compact ? " is-compact" : ""}${light ? " is-light" : ""}`}
    >
      {features.map(({ icon: Icon, title, text }) => (
        <div className="channel-feature-item" key={title}>
          <Icon size={compact ? 18 : 22} strokeWidth={1.7} aria-hidden="true" />
          <div>
            <strong>{title}</strong>
            <span>{text}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionHeading({ number, english, title, description, note, features }) {
  return (
    <header className="channel-section-heading">
      <p className="channel-section-kicker">
        {number}　{english}
      </p>
      <h2>{title}</h2>
      <span className="channel-section-rule" aria-hidden="true" />
      <p className="channel-section-description">{description}</p>
      {note ? <p className="channel-section-note">{note}</p> : null}
      {features?.length ? <FeatureRow features={features} compact /> : null}
    </header>
  );
}

function ProductCard({ product, onOpenDetails }) {
  return (
    <article className="channel-product-card">
      <div className="channel-product-image">
        <img
          src={`${ASSET_BASE}/${product.image}`}
          alt={`${product.name}の製品例`}
          loading="lazy"
        />
      </div>

      <div className="channel-product-copy">
        {product.englishName ? (
          <span className="channel-product-english">{product.englishName}</span>
        ) : null}

        <h3>{product.name}</h3>

        <p className="channel-product-description">{product.description}</p>

        {product.specification || product.note ? (
          <div className="channel-product-spec-box">
            {product.specification ? (
              <p className="channel-product-specification">{product.specification}</p>
            ) : null}
            {product.note ? <p className="channel-product-note">{product.note}</p> : null}
          </div>
        ) : null}

        <button
          className="channel-detail-button"
          type="button"
          onClick={() => onOpenDetails(product)}
          aria-label={`${product.name}の仕様・詳細画像を表示`}
        >
          <span>仕様・詳細を見る</span>
          <ArrowRight size={15} strokeWidth={1.7} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

function SpecificationModal({ product, onClose, closeButtonRef }) {
  if (!product) return null;

  const titleId = `channel-spec-title-${product.detailsImage.replace(/\W/g, "-")}`;
  const imagePath = `${SPEC_BASE}/${product.detailsImage}`;

  return (
    <div
      className="channel-spec-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="channel-spec-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="channel-spec-modal-header">
          <div>
            <p>SPECIFICATION / DETAILS</p>
            <h2 id={titleId}>{product.name}</h2>
          </div>

          <button
            className="channel-spec-modal-close"
            type="button"
            onClick={onClose}
            ref={closeButtonRef}
            aria-label="仕様・詳細を閉じる"
          >
            <X size={24} strokeWidth={1.6} aria-hidden="true" />
          </button>
        </header>

        <div className="channel-spec-modal-image-wrap">
          <img
            className="channel-spec-modal-image"
            src={imagePath}
            alt={`${product.name}の仕様・詳細`}
          />
        </div>

        <footer className="channel-spec-modal-footer">
          <p>画像を拡大して、寸法・仕様・製作例をご確認いただけます。</p>
          <a href={imagePath} target="_blank" rel="noreferrer">
            別画面で画像を開く
            <ExternalLink size={14} strokeWidth={1.7} aria-hidden="true" />
          </a>
        </footer>
      </section>
    </div>
  );
}

function matchesFilter(product, activeFilter) {
  return !activeFilter || product.tags.includes(activeFilter);
}

export default function ChannelLetters() {
  const [activeFilter, setActiveFilter] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stickyToolsActive, setStickyToolsActive] = useState(false);
  const [stickyToolsHeight, setStickyToolsHeight] = useState(0);
  const [stickyToolsTop, setStickyToolsTop] = useState(0);

  const closeButtonRef = useRef(null);
  const stickySentinelRef = useRef(null);
  const stickyToolsRef = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    const description =
      "チャンネル文字、LEDチャンネル文字、バックライト、側面発光、LEDネオン、3Dチャンネルなどを取り扱っています。金属・樹脂・アクリルなどの素材、発光方法、サイズ、設置条件に合わせてご提案します。";
    let metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute("content") ?? null;
    const createdMetaDescription = !metaDescription;

    document.title =
      "チャンネル文字・LEDチャンネル文字・LEDネオン｜看板・サイン｜株式会社エイチエス";

    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute("content", description);

    return () => {
      document.title = previousTitle;

      if (createdMetaDescription) {
        metaDescription.remove();
      } else if (previousDescription !== null) {
        metaDescription.setAttribute("content", previousDescription);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedProduct) return undefined;

    const previousOverflow = document.body.style.overflow;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelectedProduct(null);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedProduct]);

  useEffect(() => {
    let frameId = null;

    const getFixedHeaderOffset = () => {
      const candidates = Array.from(
        document.querySelectorAll(
          "header, #header, .site-header, .global-header, .main-header",
        ),
      );

      let offset = 0;

      candidates.forEach((element) => {
        if (stickyToolsRef.current?.contains(element)) return;

        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        const positioned = style.position === "fixed" || style.position === "sticky";

        if (positioned && rect.bottom > 0 && rect.top <= 2) {
          offset = Math.max(offset, rect.bottom);
        }
      });

      return Math.max(0, Math.round(offset));
    };

    const updateStickyTools = () => {
      frameId = null;

      const sentinel = stickySentinelRef.current;
      const tools = stickyToolsRef.current;

      if (!sentinel || !tools) return;

      /* On smaller screens the category navigation is horizontally scrollable,
         so keeping both bars fixed would consume too much viewport height. */
      if (window.innerWidth <= 820) {
        setStickyToolsActive(false);
        setStickyToolsHeight(0);
        setStickyToolsTop(0);
        return;
      }

      const headerOffset = getFixedHeaderOffset();
      const sentinelTop = sentinel.getBoundingClientRect().top;
      const shouldFix = sentinelTop <= headerOffset;

      setStickyToolsTop(headerOffset);
      setStickyToolsActive(shouldFix);
      setStickyToolsHeight(shouldFix ? tools.offsetHeight : 0);
    };

    const requestUpdate = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateStickyTools);
    };

    updateStickyTools();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, []);

  const visibleChannelLetters = useMemo(
    () => channelLetters.filter((product) => matchesFilter(product, activeFilter)),
    [activeFilter],
  );

  const visibleLedChannelLetters = useMemo(
    () => ledChannelLetters.filter((product) => matchesFilter(product, activeFilter)),
    [activeFilter],
  );

  const visibleLedNeon3d = useMemo(
    () => ledNeon3d.filter((product) => matchesFilter(product, activeFilter)),
    [activeFilter],
  );

  const hasResults =
    visibleChannelLetters.length +
      visibleLedChannelLetters.length +
      visibleLedNeon3d.length >
    0;

  const selectFilter = (filter) => {
    setActiveFilter((current) => (current === filter ? "" : filter));
  };

  return (
    <main className="hs-channel-page" id="page-top">
      <section
        className="channel-hero"
        aria-labelledby="channel-page-title"
        style={{
        "--channel-hero-bg": `url("${ASSET_BASE}/channel-hero-technical-bg.png")`,
        }}
      >
        <div className="channel-container channel-hero-grid">
          <div className="channel-hero-copy">
            <nav className="channel-breadcrumb" aria-label="パンくずリスト">
              <Link to="/products/signage">
                <ArrowLeft size={13} strokeWidth={1.7} aria-hidden="true" />
                看板・サイン
              </Link>
              <span aria-hidden="true">／</span>
              <span>チャンネル文字</span>
            </nav>

            <p className="channel-hero-label">CHANNEL LETTERS</p>

            <h1 id="channel-page-title">チャンネル文字</h1>

            <span className="channel-hero-rule" aria-hidden="true" />

            <p className="channel-hero-description">
              チャンネル文字は、店舗看板や施設サイン、ロゴ・名称表示などに使用される
              立体文字です。金属・樹脂・アクリルなどの素材、表面仕上げ、正面・背面・
              側面などの発光方式から、デザインや設置条件に合わせた仕様をお選びいただけます。
            </p>

            <FeatureRow features={heroFeatures} light />

            <a className="channel-hero-button" href="#product-finder">
              取扱製品を見る
            </a>
          </div>

          <figure className="channel-hero-visual">
            <img
              src={`${ASSET_BASE}/channel-hero.png`}
              alt="金属文字、LEDチャンネル文字、LEDネオンなどの製品例"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </figure>
        </div>
      </section>

      <div
        ref={stickySentinelRef}
        className="channel-sticky-sentinel"
        aria-hidden="true"
      />

      <div
        className="channel-sticky-placeholder"
        aria-hidden="true"
        style={{ height: stickyToolsHeight }}
      />

      <div
        ref={stickyToolsRef}
        className={`channel-sticky-tools${stickyToolsActive ? " is-fixed" : ""}`}
        style={stickyToolsActive ? { top: stickyToolsTop } : undefined}
      >
        <nav className="channel-category-nav" aria-label="製品カテゴリ">
          <div className="channel-container channel-category-nav-inner">
            <p className="channel-category-nav-title">PRODUCT CATEGORY</p>

            <div className="channel-category-links">
              {categoryLinks.map((category) => (
                <a href={`#${category.target}`} key={category.target}>
                  <span className="channel-category-number">{category.number}</span>
                  <span>
                    <strong>{category.label}</strong>
                    <small>{category.count}</small>
                  </span>
                  <span className="channel-category-arrow" aria-hidden="true">
                    ↓
                  </span>
                </a>
              ))}
            </div>

            <a className="channel-category-contact" href="#channel-contact">
              お問い合わせ
            </a>
          </div>
        </nav>

        <section className="channel-product-finder" id="product-finder">
          <div className="channel-container channel-finder-grid">
            <div>
              <p className="channel-eyebrow">PRODUCT FINDER</p>
              <h2>光り方・素材から絞り込む</h2>
              <p>仕上がりのイメージを選ぶと、該当する製品を確認できます。</p>
            </div>

            <div className="channel-filter-list" aria-label="製品の絞り込み">
              {filters.map((filter) => {
                const active = activeFilter === filter;

                return (
                  <button
                    className={active ? "is-active" : ""}
                    type="button"
                    aria-pressed={active}
                    onClick={() => selectFilter(filter)}
                    key={filter}
                  >
                    <span className="channel-filter-dot" aria-hidden="true" />
                    {filter}
                    <span className="channel-filter-symbol" aria-hidden="true">
                      {active ? "−" : "＋"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {!hasResults ? (
        <section className="channel-empty-state" aria-live="polite">
          <h2>該当する製品がありません</h2>
          <button type="button" onClick={() => setActiveFilter("")}>
            すべての製品を表示
          </button>
        </section>
      ) : null}

      {visibleChannelLetters.length ? (
        <section className="channel-product-section" id="channel-letters">
          <div className="channel-container">
            <SectionHeading
              number="01"
              english="MATERIAL & FINISH"
              title="素材・仕上げから選ぶ"
              description="店舗看板、施設サイン、標札、名称表示などに使用する立体文字を、素材や質感から選べます。箱文字、真鍮文字、ステンレス文字、樹脂メタリック文字、カルプ文字、アクリル文字など、金属の質感を生かした仕様から軽量な樹脂系文字まで、用途や設置環境に応じて選択できます。"
              features={materialFeatures}
            />

            <div className="channel-product-grid">
              {visibleChannelLetters.map((product) => (
                <ProductCard
                  product={product}
                  onOpenDetails={setSelectedProduct}
                  key={product.name}
                />
              ))}
            </div>

            <a className="channel-back-to-top" href="#page-top">
              ページ上部へ ↑
            </a>
          </div>
        </section>
      ) : null}

      {visibleLedChannelLetters.length ? (
        <section
          className="channel-product-section channel-product-section-pale"
          id="led-channel-letters"
        >
          <div className="channel-container">
            <SectionHeading
              number="02"
              english="LED CHANNEL LETTERS"
              title="LEDチャンネル文字"
              description="LEDチャンネル文字は、正面発光、バックライト、側面発光、両面発光など、光の見え方から選べる立体サインです。縁ありチャンネル、スリムレター、レターネオン、カラーアンドホワイト、マーキーサイン、チューブネオンなど、文字やロゴの見せ方に応じた各種仕様を取り扱っています。"
              note="すべて受注生産です。書体・サイズ・設置条件を確認して製作します。"
              features={ledFeatures}
            />

            <div className="channel-product-grid">
              {visibleLedChannelLetters.map((product) => (
                <ProductCard
                  product={product}
                  onOpenDetails={setSelectedProduct}
                  key={product.name}
                />
              ))}
            </div>

            <a className="channel-back-to-top" href="#page-top">
              ページ上部へ ↑
            </a>
          </div>
        </section>
      ) : null}

      {visibleLedNeon3d.length ? (
        <section className="channel-product-section" id="led-neon-3d">
          <div className="channel-container">
            <SectionHeading
              number="03"
              english="LED NEON / 3D"
              title="LEDネオン・3D"
              description="LEDネオンと3D造形を組み合わせ、立体感や発光表現を生かしたサインを製作します。側面発光、テーパー形状、3Dインフィニティミラー、カラー樹脂、PC一体型、3Dモデリング、ガルバフレーム、表シート貼り、シルクタイプ、側面3D、パターン模様3Dなど、多様な製作方法と表面表現に対応します。"
              features={neon3dFeatures}
            />

            <div className="channel-product-grid">
              {visibleLedNeon3d.map((product) => (
                <ProductCard
                  product={product}
                  onOpenDetails={setSelectedProduct}
                  key={product.name}
                />
              ))}
            </div>

            <a className="channel-back-to-top" href="#page-top">
              ページ上部へ ↑
            </a>
          </div>
        </section>
      ) : null}

      <section className="channel-order-made">
        <div className="channel-container">
          <p className="channel-eyebrow">ORDER FLOW</p>
          <h2>製作について</h2>
          <span className="channel-order-rule" aria-hidden="true" />

          <p className="channel-order-introduction">
            ご希望の文字・ロゴ、サイズ、設置場所、発光方法などを確認し、
            製作仕様とお見積りをご案内します。
          </p>

          <ol className="channel-order-list">
            <li>
              <span>01</span>
              <strong>お問い合わせ</strong>
              <small>製品種類、サイズ、数量、設置場所などをお知らせください</small>
            </li>
            <li>
              <span>02</span>
              <strong>データ・仕様確認</strong>
              <small>文字・ロゴデータ、仕上げ、発光方法、設置条件を確認します</small>
            </li>
            <li>
              <span>03</span>
              <strong>お見積り・製作</strong>
              <small>仕様確定後、内容に合わせて製作を進めます</small>
            </li>
            <li>
              <span>04</span>
              <strong>納品・施工</strong>
              <small>完成後、内容を確認のうえ納品・施工へ進みます</small>
            </li>
          </ol>
        </div>
      </section>

      <section className="channel-contact" id="channel-contact">
        <div className="channel-container channel-contact-panel">
          <div>
            <p className="channel-eyebrow">CONTACT</p>
            <h2>チャンネル文字の製作・お見積り</h2>
            <p>
              仕様・サイズ・設置条件に合わせてご提案します。
              ロゴや文字データをお持ちの場合は、あわせてお送りください。
            </p>

            <Link to="/contact">お問い合わせフォームへ</Link>
          </div>

          <dl className="channel-contact-details">
            <div>
              <dt>お電話でのお問い合わせ</dt>
              <dd>
                <a href="tel:0368250852">03-6825-0852</a>
              </dd>
            </div>

            <div>
              <dt>メールでのお問い合わせ</dt>
              <dd>
                <a href="mailto:kim@hs-i.co.jp">kim@hs-i.co.jp</a>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <SpecificationModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
        closeButtonRef={closeButtonRef}
      />
    </main>
  );
}
